import * as React from 'react';

import { useSetAtom } from 'jotai';

import { useCustomerPortalUserRoleAssignmentsQuery } from '../_graphql/useCustomerPortalUserRoleAssignmentsQuery';
import { useExternalRolesQuery } from '../_graphql/useExternalRolesQuery';

import {
  $updateCpUserRoleForm,
  type CpUserRoleAssignment,
} from '~/modules/settings/_state/customerPortalUserForm.state';

export function useInitCustomerPortalUserRoleFormFromApi(
  userId: string | undefined,
) {
  const updateRoleForm = useSetAtom($updateCpUserRoleForm);

  const {
    data: { roles = [] } = {},
    isSuccess: isRolesSuccess,
    isFetching: isRolesFetching,
  } = useExternalRolesQuery({
    limit: 1000,
    offset: 0,
  });

  const {
    data: assignmentsData,
    isSuccess: isAssignmentsSuccess,
    isFetching: isAssignmentsFetching,
  } = useCustomerPortalUserRoleAssignmentsQuery({
    userId: userId ?? '',
  });
  const rawAssignments = assignmentsData?.assignments ?? [];

  const prevAssignmentsRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!userId || roles.length === 0) return;

    const assignmentsKey = JSON.stringify(rawAssignments);
    if (assignmentsKey === prevAssignmentsRef.current) return;
    prevAssignmentsRef.current = assignmentsKey;

    const roleFacilityMap = new Map<string, string[]>();
    rawAssignments.forEach((a) => {
      const existing = roleFacilityMap.get(a.roleId) ?? [];
      existing.push(a.facilityId);
      roleFacilityMap.set(a.roleId, existing);
    });

    const initialAssignments: CpUserRoleAssignment[] = roles.map((role) => ({
      roleId: role.id,
      roleName: role.name,
      userCount: role.userCount,
      assignedFacilityIds: roleFacilityMap.get(role.id) ?? [],
      lastUpdatedAt: null,
      lastUpdatedBy: null,
    }));

    updateRoleForm({
      assignments: initialAssignments,
      rawAssignments: rawAssignments.map((a) => ({
        id: a.id,
        roleId: a.roleId,
        facilityId: a.facilityId,
      })),
    });
  }, [userId, roles, rawAssignments, updateRoleForm]);

  const hasRoleFormLoaded =
    !!userId &&
    isRolesSuccess &&
    isAssignmentsSuccess &&
    !isRolesFetching &&
    !isAssignmentsFetching;

  return { hasRoleFormLoaded };
}
